import { Github, Linkedin, Instagram, Mail, ExternalLink, Heart, MapPin } from 'lucide-react';
import GlassCard from '../ui/GlassCard';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  const socialLinks = [
    { 
      name: 'GitHub',
      icon: <Github className="w-5 h-5" />,
      url: 'https://github.com/Seelam3653',
      ariaLabel: 'Visit my GitHub profile'
    },
    { 
      name: 'LinkedIn',
      icon: <Linkedin className="w-5 h-5" />,
      url: 'https://linkedin.com/in/manosaiseelam',
      ariaLabel: 'Connect with me on LinkedIn'
    },
    { 
      name: 'Instagram',
      icon: <Instagram className="w-5 h-5" />,
      url: 'https://www.instagram.com/_.sai.__._/',
      ariaLabel: 'Follow me on Instagram'
    },
    { 
      name: 'Email',
      icon: <Mail className="w-5 h-5" />,
      url: 'mailto:manosai2002@gmail.com',
      ariaLabel: 'Send me an email'
    }
  ];

  return (
    <footer className="relative py-16 px-6 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-violet-500/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-1/3 h-1/3 bg-gradient-to-tl from-blue-500/10 to-transparent rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* About Column */}
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-violet-300 to-blue-300 bg-clip-text text-transparent">
              Mano<span className="text-white">.</span>
            </h3>
            <p className="text-slate-300 mb-4 max-w-md">
              A passionate full-stack web developer with expertise in React, Node.js, 
              and modern web technologies. Let's build something amazing together.
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((link) => (
                <a 
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.ariaLabel}
                  className="p-2 rounded-full bg-slate-800/70 text-slate-300 hover:text-white hover:bg-gradient-to-r hover:from-violet-600/90 hover:to-blue-600/90 transition-all duration-300 border border-slate-700/50 shadow-md shadow-slate-950/50 hover:shadow-violet-500/20"
                >
                  {link.icon}
                </a>
              ))}
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-white">Quick Links</h4>
            <ul className="space-y-2">
              {['About', 'Skills', 'Projects', 'Education', 'Contact'].map((item) => (
                <li key={item}>
                  <a 
                    href={`#${item.toLowerCase()}`}
                    className="text-slate-300 hover:text-white hover:bg-gradient-to-r hover:from-violet-400 hover:to-blue-400 hover:bg-clip-text hover:text-transparent transition-colors duration-300 flex items-center"
                  >
                    <span className="w-1 h-1 bg-violet-400 rounded-full mr-2"></span>
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-white">Contact Info</h4>
            <ul className="space-y-3">
              <li>
                <a 
                  href="mailto:your.email@example.com"
                  className="text-slate-300 hover:text-white transition-colors duration-300 flex items-center"
                >
                  <Mail className="w-4 h-4 mr-2 text-violet-400" />
                  manosai2002@gmail.com
                </a>
              </li>
              <li className="flex items-center text-slate-300">
                <MapPin className="w-4 h-4 mr-2 text-blue-400" />
                Hyderabad, India
              </li>
              <li>
                <a 
                  href="https://yourportfolio.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-white transition-colors duration-300 flex items-center"
                >
                  <ExternalLink className="w-4 h-4 mr-2 text-cyan-400" />
                  yourportfolio.com
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Newsletter Subscription */}
        <GlassCard 
          color="mixed" 
          variant="flat" 
          glow={true} 
          padding="md"
          rounded="xl"
          className="mb-12"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-2">
            <div>
              <h4 className="text-lg font-semibold mb-1 text-white">Stay Updated</h4>
              <p className="text-slate-300 text-sm">Subscribe to my newsletter for latest updates</p>
            </div>
            <div className="flex w-full md:w-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="px-4 py-2 bg-slate-800/70 border border-slate-700/50 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-white w-full md:w-64"
              />
              <button
                className="px-4 py-2 bg-gradient-to-r from-violet-600 to-blue-600 text-white rounded-r-lg hover:from-violet-500 hover:to-blue-500 transition-all duration-300"
              >
                Subscribe
              </button>
            </div>
          </div>
        </GlassCard>
        
        {/* Copyright and Credits */}
        <div className="border-t border-slate-800 pt-8 flex flex-col items-center">
          <div className="text-center">
            <p className="text-slate-400 mb-2">
              © {currentYear} Mano Sai Seelam. All rights reserved.
            </p>
            <p className="text-slate-500 text-sm flex items-center justify-center">
              Made with <Heart className="w-4 h-4 mx-1 text-pink-500 animate-pulse" /> using React & Tailwind CSS
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
